import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

interface Flight {
    "company name": string;
    date: string;
    price: number;
    destination: string;
}

interface AvgFlightPriceProps {
    data: {
        flightsData: Flight[];
    };
}

enum Airlines {
    EL_AL = "El Al Israel",
    OTHER = "Other Airlines"
}

const monthlyComparison: Record<string, number[]> = {
    [Airlines.EL_AL]: [],
    [Airlines.OTHER]: []
};
interface MonthlyPrices {
    [month: string]: {
        [Airlines.EL_AL]: number[];
        [Airlines.OTHER]: number[];
    };
}


const processFlightData = (data: Flight[]) =>  {
    const monthlyPrices:MonthlyPrices = {};
    const elAl = Airlines.EL_AL;

    data.forEach(flight => {
        const date = new Date(flight.date);
        const month:string = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}`;
        const airline = flight['company name'];

        if (!monthlyPrices?.[month]) {
            monthlyPrices[month] = { [Airlines.EL_AL]: [], [Airlines.OTHER]: [] };
        }

        if (airline === elAl) {
            monthlyPrices[month][Airlines.EL_AL].push(flight.price);
        } else {
            monthlyPrices[month][Airlines.OTHER].push(flight.price);
        }
    });

    const labels = Object.keys(monthlyPrices).sort();
    const elAlData:any = [];
    const otherAirlinesData:any[] = [];

    labels.forEach(month => {
        const elAlPrices = monthlyPrices[month][Airlines.EL_AL];
        const otherPrices = monthlyPrices[month][Airlines.OTHER];

        const elAlAverage = elAlPrices.reduce((sum, price) => sum + price, 0) / elAlPrices.length || 0;
        const otherAverage = otherPrices.reduce((sum, price) => sum + price, 0) / otherPrices.length || 0;

        elAlData.push(elAlAverage);
        otherAirlinesData.push(otherAverage);
    });

    return { labels, elAlData, otherAirlinesData };
};

const AvgFlightPrice: React.FC<AvgFlightPriceProps> = ({ data }) => {
    const { flightsData } = data;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartInstanceRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        const { labels, elAlData: elAlPrices, otherAirlinesData: otherPrices } = processFlightData(flightsData);

        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                chartInstanceRef.current = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: labels,
                        datasets: [
                            {
                                label: 'Client',
                                data: elAlPrices,
                                backgroundColor: '#ffac26',
                                borderColor: '#ff9e00',
                                borderWidth: 1
                            },
                            {
                                label: 'Market',
                                data: otherPrices,
                                backgroundColor: '#2f5e9299',
                                borderColor: 'blue',
                                borderWidth: 1
                            }
                        ]
                    },
                    options: {
                        plugins: {
                            title: {
                                display: true,
                                text: 'Price vs market trend',
                                font: {
                                    size: 20
                                },
                                padding: {
                                    top: 20,
                                    bottom: 30
                                }
                            },
                            legend: {
                                display: true,
                                position: 'bottom',
                                labels: {
                                    font: {
                                        size: 14
                                    },
                                    usePointStyle: true,
                                    padding: 20
                                }
                            },
                            tooltip: {
                                callbacks: {
                                    label: function(context) {
                                        // @ts-ignore
                                        return `${context.dataset.label}: $${context.raw.toFixed(2)}`;
                                    }
                                }
                            }
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                title: {
                                    display: true,
                                    text: 'Average Price',
                                    font: {
                                        size: 16
                                    }
                                }
                            },
                            x: {
                                title: {
                                    display: true,
                                    text: 'Month',
                                    font: {
                                        size: 16
                                    }
                                }
                            }
                        }
                    }
                });
            }
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [flightsData]);

    return (
        <div>
            <canvas ref={canvasRef} width="800" height="800"></canvas>
        </div>
    );
};

export default AvgFlightPrice;
